import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Statistic, Table, Button, Input, Select, Tag, Progress, Tabs, Space, Typography, Divider, Alert, message, Drawer, Form, InputNumber, } from 'antd';
import { DashboardOutlined, UserOutlined, BankOutlined, FileTextOutlined, DownloadOutlined, PlusOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, SearchOutlined, MoneyCollectOutlined, TeamOutlined, WalletOutlined, RiseOutlined, ExportOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { buildEcrBundleZip } from './ecrDummy';
import PfEmployeeModal from './modals/PfEmployeeMappingModal';
import { AppDispatch } from '../../core/data/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllPfEmployees } from '../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions';
import { fetchAllPfWithdrawals, updatePfWithdrawal } from '../../core/data/redux/payroll/Accounting/providentFund/pfWithdrawal/pfWithdrawalActions';
import { fetchAllPfLoans, updatePfLoan } from '../../core/data/redux/payroll/Accounting/providentFund/pfLoanRequest/pfLoanActions';
import { fetchAllProvidentFunds, fetchPFMonthlySummary } from '../../core/data/redux/payroll/Accounting/providentFund/providentFundActions';
import { Link } from 'react-router-dom';
import ProvidentFundModal from '../accounting/modals/provident_fund_Modal';
import { fetchAllPfDues, updatePfDueIsProcessed } from '../../core/data/redux/payroll/Accounting/providentFund/pfDue/pfDueActions';
import { useAppConfig } from '../../customhooks/useAppConfig';
import { formatCurrency, formatDate } from '../../core/utils/configFormatters';
import { addAnnualInterest, fetchInterestSummaryByYear } from '../../core/data/redux/payroll/Accounting/providentFund/pfLedger/pfLedgerAction';
import GPF_Nomination from "./GPF_Nomination_Form.pdf";
import GPF_FinalWithdrawal from "./GPF_Final_Withdrawal_Form.pdf";
const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const getUtcYearMonth = (iso: string | Date) => {
    const d = new Date(iso);
    return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 }; // month: 1..12
};

// Local (IST) month-year from ISO (good for requestDate fields)
const getLocalYearMonth = (iso: string | Date) => {
    const d = new Date(iso);
    return { year: d.getFullYear(), month: d.getMonth() + 1 }; // month: 1..12
};


const statusColorMap: Record<string, string> = {
    Pending: "#f59e0b",
    Approved: "#10b981",
    Rejected: "#ef4444",
    Disbursed: "#3b82f6",
    Processing: "#6366f1",
};

const PfAdminDashboard: React.FC = () => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const { user } = useSelector((state: any) => state.auth);
    const dispatch = useDispatch<AppDispatch>();
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().toLocaleString('default', { month: 'long' });
    const years = Array.from({ length: 14 }, (_, index) => currentYear - index);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedInterestRows, setSelectedInterestRows] = useState<any[]>([]);
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [modalType, setModalType] = useState('');
    const [employeeStatus, setEmployeeStatus] = useState<'all' | 'active' | 'inactive'>('active');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { records: pfEmployee } = useSelector((state: any) => state.providentFund.pfEmployeeMapping);
    const { records: pfWithdrawal } = useSelector((state: any) => state.providentFund.pfWithdrawl);
    const { records: pfLoanRequest } = useSelector((state: any) => state.providentFund.pfLoanRequest);
    const { records: pfContribution } = useSelector((state: any) => state.providentFund.employeeContribution);
    const { records: pfDues } = useSelector((state: any) => state.providentFund.pfDues);
    const { interestSummary } = useSelector((state: any) => state.providentFund.pfLedger);
    const [generating, setGenerating] = useState(false);
    const getMonthNumber = (monthName: string): number => {
        const monthIndex = new Date(`${monthName} 1, 2000`).getMonth();
        return monthIndex + 1;
    };
    const [searchText, setSearchText] = useState('');

    // Filter employees based on search text (case-insensitive search on employeeName)
    const filteredEmployees = useMemo(() => {
        const list = Array.isArray(pfEmployee) ? pfEmployee : [];
        const bySearch = !searchText
            ? list
            : list.filter((emp: any) =>
                String(emp.employeeName || '')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
            );

        if (employeeStatus === 'all') return bySearch;
        return bySearch.filter(
            (emp: any) => String(emp.status || '').toLowerCase() === employeeStatus
        );
    }, [pfEmployee, searchText, employeeStatus]);


    const monthNumber = getMonthNumber(selectedMonth);


    // Use the same filter for the Contribution *table* (you already filtered for the dashboard card)
    const filteredContributionsTable = useMemo(() => {
        return (pfContribution ?? []).filter((c: any) => {
            return Number(c.year) === Number(selectedYear) && Number(c.month) === Number(monthNumber);
        });
    }, [pfContribution, selectedYear, monthNumber]);

    // Dues: use UTC to interpret the stored "month" ISO
    const filteredPfDues = useMemo(() => {
        return (pfDues ?? []).filter((d: any) => {
            if (!d?.month) return false;
            const { year, month } = getUtcYearMonth(d.month);
            return year === Number(selectedYear) && month === Number(monthNumber);
        });
    }, [pfDues, selectedYear, monthNumber]);

    // Loans: filter by requestDate (local is fine here)
    const filteredPfLoanRequest = useMemo(() => {
        return (pfLoanRequest ?? []).filter((r: any) => {
            if (!r?.requestDate) return false;
            const { year, month } = getLocalYearMonth(r.requestDate);
            return year === Number(selectedYear) && month === Number(monthNumber);
        });
    }, [pfLoanRequest, selectedYear, monthNumber]);

    // Withdrawals: filter by requestDate (local)
    const filteredPfWithdrawal = useMemo(() => {
        return (pfWithdrawal ?? []).filter((r: any) => {
            if (!r?.requestDate) return false;
            const { year, month } = getLocalYearMonth(r.requestDate);
            return year === Number(selectedYear) && month === Number(monthNumber);
        });
    }, [pfWithdrawal, selectedYear, monthNumber]);
    const config = useAppConfig();

    const [form] = Form.useForm();

    const handleCalculateInterest = async () => {
        const values = await form.validateFields();
        try {
            let successCount = 0;

            for (let record of selectedInterestRows) {
                try {
                    const result = await dispatch(addAnnualInterest({
                        pfEmployeeId: record.pfEmployeeId,
                        interestRate: values.interestRate,
                        createdBy: user.UserId,
                        year: selectedYear,
                    })).unwrap();

                    message.success(`Success for ${record.name || record.employeeName}: ${result?.message || 'Interest added successfully'}`);
                    successCount++;
                } catch (err: any) {
                    const errMsg = err?.error || err?.message || 'Failed to add interest';
                    message.error(`For ${record.name || record.employeeName}: ${errMsg}`);
                }
            }

            if (successCount > 0) {
                setIsDrawerVisible(false);
                setSelectedInterestRows([]);
            }

        } catch (outerError) {
            message.error('Unexpected error during bulk update.');
        }
    };

    // wherever your click handler lives
    const handleGenerateEcrReports = async () => {
        try {
            setGenerating(true);

            const extraFiles = [
                { name: "GPF_Nomination_Form.pdf", url: GPF_Nomination },
                { name: "GPF_Final_Withdrawal_Form.pdf", url: GPF_FinalWithdrawal },
            ];


            const blob = await buildEcrBundleZip(selectedMonth, selectedYear, extraFiles);
            const stamp = `${selectedMonth}_${selectedYear}`;
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `EPFO_ECR_${stamp}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);

            message.success("Dummy ECR bundle (with PDFs) generated.");
        } catch (err) {
            console.error(err);
            message.error("Failed to generate ECR bundle.");
        } finally {
            setGenerating(false);
        }
    };

    const filteredContributions = pfContribution?.filter(
        (item: any) => item.year === selectedYear && item.month === monthNumber
    );
    const { pfSummary } = useSelector((state: any) => state.providentFund.employeeContribution);

    const handleYearSelect = (year: string) => {
        setSelectedYear(Number(year));
        setSelectedMonth(currentMonth);
    };

    const handleMonthSelect = (month: string) => {
        setSelectedMonth(month);
    };
    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };


    // Fetch PF Employees
    useEffect(() => {
        if (!pfEmployee.length) {
            dispatch(fetchAllPfEmployees());
        }
    }, [pfEmployee.length, dispatch]);

    // Fetch PF Withdrawals
    useEffect(() => {
        if (!pfWithdrawal.length) {
            dispatch(fetchAllPfWithdrawals());
        }
    }, [pfWithdrawal.length, dispatch]);

    // Fetch PF Loan Requests
    useEffect(() => {
        if (!pfLoanRequest.length) {
            dispatch(fetchAllPfLoans());
        }
    }, [pfLoanRequest.length, dispatch]);

    // Fetch PF Contributions
    useEffect(() => {
        if (!pfContribution.length) {
            dispatch(fetchAllProvidentFunds());
        }
    }, [pfContribution.length, dispatch]);

    // Fetch PF Dues
    useEffect(() => {
        if (!pfDues.length) {
            dispatch(fetchAllPfDues());
        }
    }, [pfDues.length, dispatch]);

    useEffect(() => {
        if (selectedYear && monthNumber) {
            dispatch(fetchPFMonthlySummary({ year: selectedYear, month: monthNumber }));
        }
    }, [dispatch, monthNumber, selectedMonth, selectedYear])

    useEffect(() => {
        if (selectedYear) {
            dispatch(fetchInterestSummaryByYear({ year: selectedYear }));
        }
    }, [dispatch, selectedYear])

    const handleStatusChange = async (value: number, record: any) => {
        try {
            await dispatch(updatePfDueIsProcessed({
                id: record.pfDueId,
                isProcessed: value,
                modifiedBy: user.UserId,
            })).unwrap();
        } catch (error) {
            console.error("Failed to update status", error);
            // Optional: show a toast or notification
        }
    };

    const handleRequestStatusChange = async (newStatus: string, record: any) => {
        try {
            const updatedData = {
                requeststatus: newStatus,
                modifiedBy: user.UserId,
            };
            await dispatch(updatePfLoan({ id: record.pfLoanId, data: updatedData }));
            await dispatch(fetchAllPfLoans())
            message.success(`Status updated to ${newStatus}`);

        } catch (error) {
            message.error("Failed to update status");
        }
    };

    const handleWithdrawalStatusChange = async (newStatus: string, record: any) => {
        try {
            const updatedData = {
                requeststatus: newStatus,
                modifiedBy: user.UserId,
            };
            await dispatch(updatePfWithdrawal({ id: record.pfWithdrawalId, data: updatedData }));
            await dispatch(fetchAllPfWithdrawals())
            message.success(`Status updated to ${newStatus}`);
        } catch (error) {
            console.error("Failed to update status", error);
            message.error("Failed to update status");
        }
    };


    // Employee Table Columns
    const employeeColumns: ColumnsType<any> = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div style={{ width: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png"; // fallback image
                        }}
                    />
                    <span>{record.employeeName}</span>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: 'PF Account',
            dataIndex: 'pfAccountNumber',
            key: 'pfAccountNumber',
        },
        {
            title: "UAN",
            dataIndex: "UAN",
            sorter: (a: any, b: any) => a.UAN?.localeCompare(b.UAN),
        },
        {
            title: "PF Account Number",
            dataIndex: "pfAccountNumber",
            sorter: (a: any, b: any) => a.pfAccountNumber?.localeCompare(b.pfAccountNumber),
        },
        {
            title: "Nominee Name",
            dataIndex: "nomineeName",
            sorter: (a: any, b: any) => a.nomineeName?.localeCompare(b.nomineeName),
        },
        {
            title: "Nominee Relation",
            dataIndex: "nomineeRelation",
            sorter: (a: any, b: any) => a.nomineeRelation?.localeCompare(b.nomineeRelation),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
                <Tag color={record.status === 'active' ? 'green' : 'red'}>
                    {record.status}
                </Tag>
            ),
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: any, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => {
                            showModal("edit");
                            setSelectedRowData(record);
                        }} data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title="Edit"
                    >
                        <i className="ti ti-edit" />
                    </button>
                </div>
            ),
        },
    ];

    const contributionColumns = [
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            render: (text: string, record: any) => (
                <div className="d-flex align-items-center">
                    <Link
                        to="#"
                        className="avatar avatar-md"
                    >
                        <img src={record.employee_picture_url ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"} alt="img" className='img-fluid rounded-circle' />
                    </Link>
                    <div className="ms-2">
                        <p className="text-dark mb-0">
                            <Link
                                to="#"
                            >{record.employee_name}
                            </Link>
                        </p>
                    </div>
                </div>
            ),
            sorter: (a: any, b: any) => a.Name.length - b.Name.length,
        },
        {
            title: "Provident Fund Type",
            dataIndex: "provident_fund_type",
            sorter: (a: any, b: any) =>
                a.provident_fund_type.length - b.provident_fund_type.length,
        },
        {
            title: "Employee Share",
            dataIndex: "employee_share_amount",
            sorter: (a: any, b: any) => parseFloat(a.employee_share_amount) - parseFloat(b.employee_share_amount),
            render: (_: any, record: any) => (
                <span>
                    {record.employee_share_amount}{" "}
                    <span style={{ color: '#6c757d' }}>
                        ({parseFloat(record.employee_share_percent)}%)
                    </span>
                </span>
            ),
        },
        {
            title: "Organization Share",
            dataIndex: "organization_share_amount",
            render: (_: any, record: any) => (
                <span>
                    {record.organization_share_amount}{" "}
                    <span style={{ color: '#6c757d' }}>
                        ({parseFloat(record.organization_share_percent)}%)
                    </span>
                </span>
            ),
            sorter: (a: any, b: any) => parseFloat(a.organization_share_amount) - parseFloat(b.organization_share_amount),
        },
    ];
    const withdrawalColumns: ColumnsType<any> = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div style={{ width: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png"; // fallback image
                        }}
                    />
                    <span>{record.employeeName}</span>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
        },
        {
            title: "Amount Requested",
            dataIndex: "amountRequested",
            render: (text: string) => formatCurrency(parseFloat(text || '0'), config),
            sorter: (a: any, b: any) => parseFloat(a.amountRequested) - parseFloat(b.amountRequested),
        },
        {
            title: "Reason",
            dataIndex: "withdrawalReason",
            sorter: (a: any, b: any) => a.withdrawalReason?.localeCompare(b.withdrawalReason),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text: any) => (
                <span
                    className={`badge ${text === "active" ? "badge-success" : "badge-danger"
                        } d-inline-flex align-items-center badge-xs`}
                >
                    <i className="ti ti-point-filled me-1" />
                    {text}
                </span>
            ),
            filters: [
                { text: "Active", value: "active" },
                { text: "Inactive", value: "inactive" },
            ],
            onFilter: (value: any, record: any) => record.status === value,
        },
        {
            title: "Request Status",
            dataIndex: "requeststatus",
            filters: [
                { text: "Pending", value: "Pending" },
                { text: "Approved", value: "Approved" },
                { text: "Rejected", value: "Rejected" },
                { text: "Paid", value: "Paid" },
            ],
            onFilter: (value: any, record: any) => record.requeststatus === value,
            render: (status: string, record: any) => (
                <Select
                    value={status}
                    onChange={(value) => handleWithdrawalStatusChange(value, record)}
                    style={{ width: 130 }}
                    size="small"
                >
                    {["Pending", "Approved", "Rejected", "Paid"].map((option) => (
                        <Select.Option key={option} value={option}>
                            <span style={{ color: statusColorMap[option], fontWeight: 500 }}>{option}</span>
                        </Select.Option>
                    ))}
                </Select>
            ),
        }
    ];

    const PfLoanColumns: ColumnsType<any> = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div className="d-flex align-items-center" style={{ minWidth: 200 }}>
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png";
                        }}
                    />
                    <span>{record.employeeName}</span>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
        },
        {
            title: "Amount Requested",
            dataIndex: "amountRequested",
            render: (text: string) => formatCurrency(parseFloat(text || '0'), config),
            sorter: (a: any, b: any) => parseFloat(a.amountRequested) - parseFloat(b.amountRequested),
        },
        {
            title: "Loan Reason",
            dataIndex: "loanReason",
            sorter: (a: any, b: any) => a.loanReason?.localeCompare(b.loanReason),
        },
        {
            title: "Tenure (Months)",
            dataIndex: "repaymentTenureMonths",
            sorter: (a: any, b: any) => a.repaymentTenureMonths - b.repaymentTenureMonths,
        },
        {
            title: "Installment (₹)",
            dataIndex: "monthlyInstallment",
            render: (text: any) => formatCurrency(parseFloat(text || 0), config),
            sorter: (a: any, b: any) => parseFloat(a.monthlyInstallment) - parseFloat(b.monthlyInstallment),
        },
        {
            title: "Request Status",
            dataIndex: "requeststatus",
            filters: [
                { text: "Pending", value: "Pending" },
                { text: "Approved", value: "Approved" },
                { text: "Rejected", value: "Rejected" },
                { text: "Paid", value: "Paid" },
            ],
            onFilter: (value: any, record: any) => record.requeststatus === value,
            render: (status: string, record: any) => (
                <Select
                    value={status}
                    onChange={(value) => handleRequestStatusChange(value, record)}
                    style={{ width: 140 }}
                    size="small"
                >
                    {["Pending", "Approved", "Rejected", "Paid"].map((statusOption) => (
                        <Select.Option key={statusOption} value={statusOption}>
                            <span style={{ color: statusColorMap[statusOption], fontWeight: 500 }}>
                                {statusOption}
                            </span>
                        </Select.Option>
                    ))}
                </Select>
            ),
        }
        ,
        {
            title: "Status",
            dataIndex: "status",
            render: (text: string) => (
                <span
                    className={`badge ${text === "active" ? "badge-success" : "badge-danger"
                        } d-inline-flex align-items-center badge-xs`}
                >
                    <i className="ti ti-point-filled me-1" />
                    {text}
                </span>
            ),
            filters: [
                { text: "Active", value: "active" },
                { text: "Inactive", value: "inactive" },
            ],
            onFilter: (value: any, record: any) => record.status === value,
        },
    ];
    const PfDuesColumns: ColumnsType<any> = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div className="d-flex align-items-center" style={{ minWidth: 200 }}>
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png";
                        }}
                    />
                    <span>{record.employeeName}</span>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: "Month",
            dataIndex: "month",
            render: (text: string) =>
                new Date(text).toLocaleString("default", { month: "long", year: "numeric" }),
            sorter: (a: any, b: any) => new Date(a.month).getTime() - new Date(b.month).getTime(),
        },
        {
            title: "Working Days",
            dataIndex: "workingDays",
            sorter: (a: any, b: any) => a.workingDays - b.workingDays,
        },
        {
            title: "Employee Share (₹)",
            dataIndex: "employeeShare",
            render: (text: any) => formatCurrency(parseFloat(text || 0), config),
            sorter: (a: any, b: any) => parseFloat(a.employeeShare) - parseFloat(b.employeeShare),
        },
        {
            title: "Employer Share (₹)",
            dataIndex: "employerShare",
            render: (text: any) => formatCurrency(parseFloat(text || 0), config),
            sorter: (a: any, b: any) => parseFloat(a.employerShare) - parseFloat(b.employerShare),
        },
        {
            title: "Total Due (₹)",
            dataIndex: "totalDue",
            render: (text: any) => (
                <span style={{ minWidth: 100, display: 'inline-block' }}>
                    {formatCurrency(parseFloat(text || 0), config)}
                </span>
            ),
            sorter: (a: any, b: any) => parseFloat(a.totalDue) - parseFloat(b.totalDue),
        },
        {
            title: "Processed",
            dataIndex: "isProcessed",
            render: (processed: number, record: any) => (
                <Select
                    value={processed}
                    onChange={(value) => handleStatusChange(value, record)}
                    style={{ width: 120 }}
                    size="small"
                >
                    {[{ label: "Yes", value: 1 }, { label: "No", value: 0 }].map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                            <span
                                style={{
                                    color: option.value === 1 ? "#28a745" : "#ffc107",
                                    fontWeight: 500,
                                }}
                            >
                                {option.label}
                            </span>
                        </Select.Option>
                    ))}
                </Select>
            ),
            filters: [
                { text: "Processed", value: 1 },
                { text: "Not Processed", value: 0 },
            ],
            onFilter: (value: any, record: any) => record.isProcessed === value,
        },
    ];

    const PfLedgerColumns: ColumnsType<any> = [
        {
            title: "Employee Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Total Contribution",
            dataIndex: "totalContribution",
            key: "totalContribution",
            render: (value: number) => formatCurrency(value, config),
            sorter: (a, b) => a.totalContribution - b.totalContribution,
        },
        {
            title: "Total Withdrawal",
            dataIndex: "totalWithdrawal",
            key: "totalWithdrawal",
            render: (value: number) => formatCurrency(value, config),
            sorter: (a, b) => a.totalWithdrawal - b.totalWithdrawal,
        },
        {
            title: "Total Loan",
            dataIndex: "totalLoan",
            key: "totalLoan",
            render: (value: number) => formatCurrency(value, config),
            sorter: (a, b) => a.totalLoan - b.totalLoan,
        },

        {
            title: "Interest Amount",
            dataIndex: "interest",
            key: "interest",
            render: (value: number) => formatCurrency(value, config),
            sorter: (a, b) => a.interest - b.interest,
        },
        {
            title: "Total Balance",
            dataIndex: "totalBalance",
            key: "totalBalance",
            render: (value: number) => (
                <span style={{ color: value < 0 ? 'red' : 'green' }}>
                    {formatCurrency(value, config)}
                </span>
            ),
            sorter: (a, b) => a.totalBalance - b.totalBalance,
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="pf-admin-dashboard">
                    {/* Header */}
                    <div className="bg-white shadow-sm border-bottom mb-4">
                        <div className="container-fluid">
                            <div className="row align-items-center py-3">
                                <div className="col">
                                    <Title level={3} className="mb-0">
                                        <BankOutlined className="me-2 text-primary" />
                                        Admin Dashboard
                                    </Title>
                                </div>
                                <div className="col-auto ms-auto d-flex align-items-center gap-2">
                                    {/* Year Dropdown */}
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-primary dropdown-toggle d-flex align-items-center"
                                            type="button"
                                            id="dropdownYearButton"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            <i className="ti ti-calendar me-2" />
                                            {selectedYear}
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby="dropdownYearButton">
                                            {years?.map((year: any, index: any) => (
                                                <li key={index}>
                                                    <a className="dropdown-item" href="#" onClick={() => handleYearSelect(year)}>
                                                        {year}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Month Dropdown */}
                                    <div className="dropdown">
                                        <button
                                            className="btn btn-primary dropdown-toggle d-flex align-items-center"
                                            type="button"
                                            id="dropdownMonthButton"
                                            data-bs-toggle="dropdown"
                                            aria-expanded="false"
                                        >
                                            <i className="ti ti-calendar me-2" />
                                            {selectedMonth}
                                        </button>
                                        <ul className="dropdown-menu" aria-labelledby="dropdownMonthButton">
                                            {[
                                                'January', 'February', 'March', 'April', 'May', 'June',
                                                'July', 'August', 'September', 'October', 'November', 'December'
                                            ].map((month, index) => (
                                                <li key={index}>
                                                    <a className="dropdown-item" href="#" onClick={() => handleMonthSelect(month)}>
                                                        {month}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="container-fluid">
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            size="large"
                            className="mb-4"
                        >
                            <TabPane tab={<span><DashboardOutlined />{" "}Dashboard</span>} key="dashboard">
                                {/* Dashboard Statistics */}
                                <Row gutter={[24, 24]} className="mb-4">
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Total Employees"
                                                value={pfSummary?.total_pf_employees}
                                                prefix={<TeamOutlined className="text-primary" />}
                                                valueStyle={{ color: '#1890ff' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Total Balance"
                                                value={formatCurrency(pfSummary?.total_pf_balance || 0, config)}
                                                suffix={<RiseOutlined className="text-success" />}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Monthly Contributions"
                                                value={formatCurrency(pfSummary?.total_share_amount || 0, config)}
                                                suffix={<MoneyCollectOutlined className="text-warning" />}
                                                valueStyle={{ color: '#faad14' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Pending Withdrawals"
                                                value={pfSummary?.pending_withdrawals}
                                                prefix={<ExclamationCircleOutlined className="text-warning" />}
                                                valueStyle={{ color: '#fa8c16' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Unprocessed Due"
                                                value={formatCurrency(pfSummary?.due_amount || 0, config)}
                                                valueStyle={{ color: '#d46b08' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <Statistic
                                                title="Active Loans"
                                                value={pfSummary?.active_loans}
                                                prefix={<WalletOutlined className="text-info" />}
                                                valueStyle={{ color: '#13c2c2' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6} xl={6}>
                                        <Card className="h-100">
                                            <div>
                                                <Text className="d-block mb-2">Compliance Rate</Text>
                                                <Progress
                                                    percent={parseFloat(pfSummary?.compliance_rate)}
                                                    status="active"
                                                    strokeColor="#52c41a"
                                                />
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Recent Activities */}
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} lg={12}>
                                        <Card title="Recent Contributions">
                                            <Table
                                                columns={contributionColumns}
                                                dataSource={filteredContributions}
                                                pagination={false}
                                                size="small"
                                            />
                                        </Card>
                                    </Col>
                                    <Col xs={24} lg={12}>
                                        <Card title="Pending Requests">
                                            <div className="mb-3">
                                                <Alert
                                                    message={`${pfSummary?.pending_withdrawals ?? 0} withdrawal request${pfSummary?.pending_withdrawals !== 1 ? 's' : ''} pending approval`}
                                                    type="warning"
                                                    showIcon
                                                    className="mb-2"
                                                />
                                                <Alert
                                                    message={`${pfSummary?.pending_loans ?? 0} loan request${pfSummary?.pending_loans !== 1 ? 's' : ''} pending for approval`}
                                                    type="warning"
                                                    showIcon
                                                />
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>

                            <TabPane tab={<span><UserOutlined />{" "}Employees</span>} key="employees">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Employee Management</Title>
                                        <Space>
                                            <Input
                                                placeholder="Search employees..."
                                                prefix={<SearchOutlined />}
                                                onChange={(e) => setSearchText(e.target.value)}
                                                style={{ width: 250 }}
                                            />
                                            <Select
                                                value={employeeStatus}
                                                onChange={(v) => setEmployeeStatus(v)}
                                                style={{ width: 150 }}
                                            >
                                                <Option value="all">All</Option>
                                                <Option value="active">Active</Option>
                                                <Option value="inactive">Inactive</Option>
                                            </Select>

                                            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('create')}>
                                                Add Employee
                                            </Button>
                                        </Space>
                                    </div>
                                    <Table
                                        columns={employeeColumns}
                                        dataSource={filteredEmployees}
                                        pagination={{ pageSize: 10 }}
                                        rowKey="pfEmployeeId"
                                    />
                                </Card>
                            </TabPane>

                            <TabPane tab={<span><MoneyCollectOutlined />{" "}Contributions</span>} key="contributions">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Monthly Contributions</Title>
                                        <Space>
                                            {/* <Select defaultValue="all" style={{ width: 120 }}>
                                                <Option value="all">All Status</Option>
                                                <Option value="paid">Paid</Option>
                                                <Option value="pending">Pending</Option>
                                            </Select> */}
                                            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal("create")}>
                                                Add Contribution
                                            </Button>
                                        </Space>
                                    </div>
                                    <Table
                                        columns={contributionColumns}
                                        dataSource={filteredContributionsTable}
                                        pagination={{ pageSize: 10 }}
                                        rowKey="pfContributionId"
                                    />
                                </Card>
                            </TabPane>

                            <TabPane tab={<span><WalletOutlined />{" "}Withdrawals</span>} key="withdrawals">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Withdrawal Requests</Title>
                                        <Space>
                                            <Select defaultValue="all" style={{ width: 120 }}>
                                                <Option value="all">All Status</Option>
                                                <Option value="pending">Pending</Option>
                                                <Option value="approved">Approved</Option>
                                                <Option value="rejected">Rejected</Option>
                                            </Select>
                                            {/* <Button type="primary" icon={<CheckCircleOutlined />}>
                                                Bulk Approve
                                            </Button> */}
                                        </Space>
                                    </div>
                                    <Table
                                        columns={withdrawalColumns}
                                        dataSource={filteredPfWithdrawal}
                                        pagination={{ pageSize: 10 }}
                                        rowKey="pfWithdrawalId"
                                    />
                                </Card>
                            </TabPane>
                            <TabPane tab={<span><MoneyCollectOutlined />{" "}Loans</span>} key="Loans">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Loan Requests</Title>
                                        <Space>
                                            <Select defaultValue="all" style={{ width: 120 }}>
                                                <Option value="all">All Status</Option>
                                                <Option value="pending">Pending</Option>
                                                <Option value="approved">Approved</Option>
                                                <Option value="rejected">Rejected</Option>
                                            </Select>
                                            {/* <Button type="primary" icon={<CheckCircleOutlined />}>
                                                Bulk Approve
                                            </Button> */}
                                        </Space>
                                    </div>
                                    <Table
                                        columns={PfLoanColumns}
                                        dataSource={filteredPfLoanRequest}
                                        pagination={{ pageSize: 10 }}
                                        rowKey="pfWithdrawalId"
                                    />
                                </Card>
                            </TabPane>
                            <TabPane tab={<span><ExportOutlined />{" "}Dues</span>} key="Dues">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Dues</Title>
                                        {/* <Space> */}
                                        {/* <Select defaultValue="all" style={{ width: 120 }}>
                                                <Option value="all">All Status</Option>
                                                <Option value="pending">Pending</Option>
                                                <Option value="approved">Approved</Option>
                                                <Option value="rejected">Rejected</Option>
                                            </Select> */}
                                        {/* <Button type="primary" icon={<CheckCircleOutlined />}>
                                                Bulk Approve
                                            </Button> */}
                                        {/* </Space> */}
                                    </div>
                                    <Table
                                        columns={PfDuesColumns}
                                        dataSource={filteredPfDues}
                                        pagination={{ pageSize: 10 }}
                                        rowKey="pfWithdrawalId"
                                    />
                                </Card>
                            </TabPane>
                            <TabPane tab={<span><MoneyCollectOutlined />{" "}Interest</span>} key="interest">
                                <Card>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <Title level={4} className="mb-0">Interest Calculation</Title>
                                        <Space>
                                            <Button type="primary" icon={<CheckCircleOutlined />} onClick={() => setIsDrawerVisible(true)} disabled={selectedInterestRows.length === 0}>
                                                Bulk Update
                                            </Button>
                                        </Space>
                                    </div>
                                    <Table
                                        rowSelection={{
                                            type: 'checkbox',
                                            onChange: (_, selectedRows) => setSelectedInterestRows(selectedRows),
                                            getCheckboxProps: (record) => ({
                                                disabled: false,
                                            }),
                                        }}
                                        columns={PfLedgerColumns}
                                        dataSource={interestSummary}
                                        pagination={{ pageSize: 10 }}
                                        rowKey={(record) => `${record.pfEmployeeId}-${record.year || selectedYear}`}
                                    />

                                </Card>
                            </TabPane>

                            <TabPane tab={<span><FileTextOutlined />{" "}Reports</span>} key="reports">
                                <Row gutter={[24, 24]}>
                                    <Col xs={24} md={12}>
                                        <Card title="Compliance Reports" className="h-100">
                                            <div className="mb-3">
                                                <Button
                                                    type="primary"
                                                    icon={<DownloadOutlined />}
                                                    className="me-2"
                                                    onClick={handleGenerateEcrReports}
                                                    disabled={generating}
                                                >
                                                    {generating ? 'Generating…' : 'ECR: Download All'}
                                                </Button>
                                            </div>
                                            <Divider />
                                            <Text>Generate monthly compliance reports for EPFO filing</Text>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>
                        </Tabs>
                    </div>
                </div>
            </div>

            <Drawer
                title="Calculate Annual Interest"
                placement="right"
                onClose={() => setIsDrawerVisible(false)}
                open={isDrawerVisible}
                width={360}
                style={{ marginTop: 50 }}
            >
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Interest Rate (%)"
                        name="interestRate"
                        rules={[{ required: true, message: 'Please enter interest rate' }]}
                    >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                    </Form.Item>

                    <Button type="primary" onClick={handleCalculateInterest} block>
                        Submit
                    </Button>
                </Form>
            </Drawer>


            {activeTab === 'employees' && (
                <PfEmployeeModal
                    modalType={modalType}
                    handleCancel={handleCancel}
                    isModalVisible={isModalVisible}
                    selectedRowData={selectedRowData}
                />
            )}

            {activeTab === 'contributions' && (
                <ProvidentFundModal
                    modalType={modalType}
                    handleCancel={handleCancel}
                    isModalVisible={isModalVisible}
                    selectedRowData={selectedRowData}
                />
            )}

        </div>
    );
};

export default PfAdminDashboard;